import {Box, Grid, Link, Paper, Stack, Typography, useTheme} from '@mui/material';
import {
  ArtifactPageType,
  CardType,
  Custom_Encode_URL,
  DocumentCategories,
  IArtifact,
  PinTypes,
  ResourceTabs,
  ProcessTabs,
} from 'app/types/Shared';
import {VariableSizeList} from 'react-window';

import {ArtifactCards} from 'components/ArtifactCards';
import {ArtifactCard} from 'components/ArtifactCards/ArtifactCard';
import {useDispatch, useSelector} from 'react-redux';

import React from 'react';
import './VariableList.css';
import {selectAllProcesses} from 'app/store/operatingModel/operatingModelSlice';
import {useNavigate} from 'react-router-dom';
import {selectUserPins} from 'app/store/users/userSlice';
import {
  setCurrentMapXL,
  setProcessPageTab,
  setProcessPageTabIndex,
  setSelectedProcess,
} from 'app/store/appState/appStateSlice';
import {useFeatureFlag} from 'configcat-react';
import IconKey from 'components/IconKey/IconKey';

type ArtifactPageProps = {
  storybook?: boolean;
  results: Array<{process: IArtifact; artifacts: Array<IArtifact>}>;
  totalResults?: number;
  activeFilters: Array<DocumentCategories>;
  type: ArtifactPageType;
  currentTab?: ResourceTabs;
  iconKey?: Array<{title: string; svg: string}>;
};
const ArtifactPageUI = ({results, type, activeFilters, storybook, currentTab, iconKey}: ArtifactPageProps) => {
  const {value: isChangeNotificationsEnabled} = useFeatureFlag('feature_change_notifications', false);
  const processes = useSelector(selectAllProcesses);
  const userPins = useSelector(selectUserPins);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [screenWidth, setScreenWidth] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup function to remove event listener when component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const navigateToProcess = (process_id?: string) => {
    const process = processes?.find((x) => x.process_id === process_id);
    if (process) {
      dispatch(setProcessPageTab(ProcessTabs.Map));
      dispatch(setProcessPageTabIndex(isChangeNotificationsEnabled ? 1 : 0));
      dispatch(setCurrentMapXL(false));
      dispatch(setSelectedProcess(process));
      navigate(`/process/${Custom_Encode_URL(process.name)}/${process.operating_models_list}`);
    }
  };

  // Function to render the process and its documents called by VariableSizeList
  const renderProcessDocuments = (
    index: number,
    data: Array<{process: IArtifact; artifacts: Array<IArtifact>}>,
    activeFilters: Array<DocumentCategories>,
  ) => {
    const item = data[index];
    return (
      <Box sx={{borderBottom: `1px solid ${theme.palette.grey[500]}`, py: 2, zIndex: -1}}>
        {item.process.title && (
          <Stack sx={{mb: 2}}>
            <Link
              sx={{cursor: 'pointer'}}
              onClick={() => navigateToProcess(item.process.key)}
              underline='hover'
              color='black'
            >
              <Typography variant='h5' sx={{mb: 2}}>
                {item.process.title}
              </Typography>
            </Link>
            {(activeFilters?.length === 0 || activeFilters.includes(DocumentCategories.Processes)) && (
              <Grid container>
                <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                  {storybook && item.process.title ? (
                    <ArtifactCard
                      data={{
                        id: item.process.id,
                        title: item.process.title,
                        created_dt: item.process.created_dt,
                        key: item.process.title,
                        url: item.process.url,
                        type: PinTypes.Process,
                        card: CardType.process,
                      }}
                      pinned={userPins?.some((x) => x.key === item.process.key) ? true : false}
                      disabled={userPins?.some((x) => x.key === item.process.key) ? false : true}
                    />
                  ) : (
                    <ArtifactCards
                      data={{
                        id: item.process.id,
                        title: item.process.title,
                        created_dt: item.process.created_dt,
                        key: item.process.key,
                        url: item.process.url,
                        type: PinTypes.Process,
                        card: CardType.process,
                      }}
                      disabled={
                        type === ArtifactPageType.Pins
                          ? userPins?.some((x) => x.key === item.process.key)
                            ? false
                            : true
                          : false
                      }
                    />
                  )}
                </Grid>
              </Grid>
            )}
          </Stack>
        )}
        {item.artifacts?.length > 0 && (
          <Box sx={{mb: 2}}>
            <Grid container spacing={2}>
              {item.artifacts.map((artifact, index) => (
                <Grid item key={`${artifact.key}-${index}`} xs='auto'>
                  <>
                    {storybook ? (
                      <ArtifactCard
                        data={{
                          ...artifact,
                          created_dt: artifact.created_dt.substring(0, 10), // truncate to date
                          card: CardType.document,
                        }}
                        pinned={true}
                      />
                    ) : (
                      <ArtifactCards
                        data={{
                          ...artifact,
                          parent_process_id: currentTab === ResourceTabs.TTDOM ? null : item.process.docid,
                          created_dt: artifact.created_dt.substring(0, 10), // truncate to date
                          card: CardType.document,
                        }}
                      />
                    )}
                  </>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

  //get preset size for each item in the list depending on the screen width
  const getItemsSize = (index: number) => {
    const getCols = (width: number) => {
      return Math.floor((width - 48) / 346); // calculate the number of grid columns based on the screen width, 346 is the width of the card 48 is the screen padding
    };

    //get the height of the filter depending on the active filter (add height for process cards)
    const getFilter = (filter: Array<DocumentCategories>) => {
      const padding = 32 + 20; //code padding and 20px fo extra spacing between sections
      if (!results[index].process.title)
        return padding + (iconKey && activeFilters?.length < 1 && iconKey?.length > 2 ? 12 * iconKey.length : 0);
      else if (filter?.length === 0 || activeFilters?.includes(DocumentCategories.Processes)) return 107 + padding;
      else return 35 + padding;
    };
    //               Number of Rows                                          * Height of each row (80% of the screen is used) + additonal height depending on the filter
    return Math.ceil(results[index].artifacts?.length / getCols(screenWidth * 0.8)) * 88 + getFilter(activeFilters);
  };

  const [isHovered, setIsHovered] = React.useState(false);

  // Render the UI
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', pb: 4, zIndex: 1}}>
      <Paper
        sx={{
          minHeight: '50vh',
          background: theme.palette.grey[100],
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
          py: 2,
          pl: 6,
          pr: 2,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <VariableSizeList
          itemSize={getItemsSize}
          width='80%'
          height={800}
          itemCount={results?.length}
          itemData={results}
          key={`${results?.length} + ${screenWidth} + ${activeFilters.length} + ${currentTab || ''}`}
          className='container'
          style={isHovered ? {} : {scrollbarWidth: 'none'}}
        >
          {({index, style, data}) => <div style={style}>{renderProcessDocuments(index, data, activeFilters)}</div>}
        </VariableSizeList>
        {!!results?.length && iconKey && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-start',
              flexGrow: 1,
              mb: -2,
              pl: 6,
              width: '20%',
            }}
          >
            <Box>
              <IconKey items={iconKey} />
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
export default ArtifactPageUI;
